/**
* @Author: Michael Neumair <mBook>
* @Date:   2016-09-22T17:40:29+02:00
* @Email:  7q7w7e7r@gmail.com
* @Last modified by:   mBook
* @Last modified time: 2016-10-06T19:36:12+02:00
*/

import createNumberMaskFromLib from 'text-mask-addons/dist/createNumberMask';
// import { conformToMask } from 'text-mask-core';

const emptyString = '';
const period = '.';
const minus = '-';
const zero = '0';


/**
 * see: https://github.com/text-mask/text-mask/tree/master/addons#createnumbermask
 * createNumberMask returns a numberMask function that will format
 * user input as currency. createNumberMask accepts an object with
 * the following keys:
 *
 * @param  {string} prefix (string): what to display before the amount. Defaults to '$'.
 * @param  {string} suffix (string): what to display after the amount. Defaults to empty string.
 * @param  {boolean} includeThousandsSeparator (boolean): whether or not to separate thousands. Defaults to to true.
 * @param  {string} thousandsSeparatorSymbol (string): character with which to separate thousands. Default to ','.
 * @param  {boolean} allowDecimal (boolean): whether or not to allow the user to enter a fraction with the amount. Default to false.
 * @param  {string} decimalSymbol (string): character that will act as a decimal point. Defaults to '.'
 * @param  {number} decimalLimit (number): how many digits to allow after the decimal. Defaults to 2
 * @param  {number} integerLimit (number): how many digits to allow before the decimal. Defaults to unlimited (null)
 * @param  {boolean} requireDecimal (boolean): whether or not to always include a decimal point and placeholder for decimal digits after the integer. Defaults to false.
 * @param  {boolean} allowNegative (boolean): whether or not to allow negative numbers. Defaults to false
 */
const defaultOptions = {
  prefix: '',
  suffix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: '.',
  allowDecimal: true,
  decimalSymbol: ',',
  decimalLimit: 2,
  integerLimit: null,
  requireDecimal: false,
  allowNegative: false,
};


/**
 * returns a function-mask
 * @param  see default options ..
 */
export function createNumberMask({
  prefix,
  suffix,
  includeThousandsSeparator,
  thousandsSeparatorSymbol,
  allowDecimal,
  decimalSymbol,
  decimalLimit,
  integerLimit,
  requireDecimal,
  allowNegative,
} = defaultOptions) {
  /**
     * returns the function-mask for a masked input
     * see: https://github.com/text-mask/text-mask/tree/master/addons#createnumbermask
     */
  return createNumberMaskFromLib({
    prefix,
    suffix,
    includeThousandsSeparator,
    thousandsSeparatorSymbol,
    allowDecimal,
    decimalSymbol,
    decimalLimit,
    integerLimit,
    requireDecimal,
    allowNegative,
  });
}


/**
 * returns a parser function
 * @param  see default options ..
 */
export function createNumberParser({
  prefix,
  suffix,
  includeThousandsSeparator,
  thousandsSeparatorSymbol,
  allowDecimal,
  decimalSymbol,
  decimalLimit,
  requireDecimal, // eslint-disable-line no-unused-vars
  allowNegative,
} = defaultOptions) {
  /**
     * takes a masked/formatted string and parses it to a Number based on the configuration
     * @param  {String} maskedInput
     * @param  {Number} parsedNumber
     */
  return (maskedInput) => {
    let value = maskedInput;
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value; // we already got a number :)
    }

    if (typeof value !== 'string' || value === emptyString) {
      // if we do not have a valid string value, return null
      return null;
    }

    if (prefix.length) {
      // removes prefix
      value = value.slice(prefix.length);
    }

    if (suffix.length) {
      // removes suffix
      value = value.slice(0, -suffix.length);
    }

    if (includeThousandsSeparator) {
      // removes thousand seperator
      value = value.split(thousandsSeparatorSymbol).join('');
    }

    if (allowDecimal) {
      // replaces decimal symbol with .
      value = value.replace(decimalSymbol, period);
    }

    let parsedValue = (allowDecimal) ? parseFloat(value) : parseInt(value, 10);
    if (Number.isNaN(parsedValue)) {
      return null; // failed parsing, return null
    }

    if (!allowNegative && parsedValue < 0) {
      parsedValue *= -1;
    }

    // crop decimals
    if (allowDecimal) {
      parsedValue *= Math.pow(10, decimalLimit);
      parsedValue = Math.round(parsedValue);
      parsedValue /= Math.pow(10, decimalLimit);
    }

    if ( allowDecimal && value[value.length - 1] == '.' ) {
      // emulate random size
      return parseFloat(parsedValue.toString() + '.001');
    }

    return parsedValue;
  };
}


/**
 * returns a formatter function
 * @param  see default options ..
 */
export function createNumberFormatter({
  prefix,
  suffix,
  includeThousandsSeparator,
  thousandsSeparatorSymbol,
  allowDecimal,
  decimalSymbol,
  decimalLimit,
  requireDecimal,
  allowNegative,
} = defaultOptions) {
  /**
     * takes a raw number value und formats it like the configuration
     * @param  {Number} rawNumber
     * @param  {STring} formattedNumber
     */
  return (rawNumber) => {
    // we use the core functions of text-mask-core to make the formatting
    // see more here: https://github.com/text-mask/text-mask/tree/master/core
    let value = '';

    if (typeof rawNumber !== 'number' || Number.isNaN(rawNumber)) {
      return '';
    }

    // use rounded value, if we do not use decimals
    const number = (allowDecimal) ? rawNumber : Math.round(rawNumber);
    const isNegative = (number < 0);

    // get string – but slice sign, we add it later ...
    const numberStr = (isNegative) ? String(number).slice(1) : String(number);

    const [predecimal, decimal] = numberStr.split(period);
    const predecimalLenght = (predecimal) ? predecimal.length : 0;

    // add thousands-seperators
    if (includeThousandsSeparator && predecimalLenght > 3) {
      for (let i = 0; i < predecimalLenght; i++) {
        const revertedIndex = predecimalLenght - i - 1;
        const currNumber = predecimal[revertedIndex];
        if (revertedIndex === 0) {
          // we add the first number
          value = `${currNumber}${value}`;
        } else if (i % 3 === 2) {
          value = `${thousandsSeparatorSymbol}${currNumber}${value}`;
        } else {
          value = `${currNumber}${value}`;
        }
      }
    } else {
      value = predecimal;
    }

    // add decimal
    if (allowDecimal) {
      // slice decimal or use empty string, if we have no decimal
      let newDecimal = (decimal === undefined) ? emptyString : decimal.slice(0, decimalLimit);

      // check the length of the decimal
      const decimalLength = newDecimal.length;

      // add required decimals
      if (requireDecimal && decimalLength < decimalLimit) {
        const missingDecimals = decimalLimit - decimalLength;
        for (let i = 0; i < missingDecimals; i++) {
          newDecimal = `${newDecimal}${zero}`;
        }
      }

      // add decimal, if we have a decimal value
      if (newDecimal !== emptyString) {
        value = `${value}${decimalSymbol}${newDecimal}`;
      }
    }


    // add negative sign
    if (isNegative && allowNegative) {
      value = `${minus}${value}`;
    }

    // add prefix
    if (prefix.length) {
      value = `${prefix}${value}`;
    }

    // add suffix
    if (suffix.length) {
      value = `${value}${suffix}`;
    }

    return value;
  };
}

export default {
  createNumberMask,
  createNumberParser,
  createNumberFormatter,
};

